<template>
  <div
    v-if="widget"
    class="hbt"
  >
    <div class="hbt-sec-head">
      <span class="hbt-h2">{{ widget.param.title || 'Здоровье' }}</span>
      <span class="hbt-kicker">{{ widget.param.subtitle || 'health' }}</span>
    </div>

    <div
      v-for="ind in indicators"
      :key="ind.id"
    >
      <div
        class="hbt-row"
        style="flex-wrap: wrap; gap: 16px;"
      >
        <div style="min-width: 130px; flex: 1;">
          <div class="hbt-h3">
            {{ ind.name }}
          </div>
          <div class="hbt-mut">
            {{ ind.en }}<span v-if="lastOf(ind)"> · {{ atOf(ind) }}</span>
          </div>
        </div>
        <div style="text-align: right; min-width: 100px;">
          <div
            class="d-flex align-items-baseline justify-content-end"
            style="gap: 5px;"
          >
            <span
              class="hbt-num"
              style="font-size: 26px;"
            >{{ valueText(ind) }}</span>
            <span
              class="hbt-mut"
              style="font-size: 11px;"
            >{{ ind.unit }}</span>
          </div>
          <div
            class="hbt-mut"
            style="font-size: 11px; margin-top: 3px;"
          >{{ trendText(ind) }}</div>
        </div>
        <span
          class="hbt-tag"
          :style="zoneStyle(ind)"
        >{{ zoneLabel(ind) }}</span>
        <button
          type="button"
          class="hbt-btn"
          style="font-size: 13px; letter-spacing: 0.02em; text-transform: uppercase;"
          @click="toggleInput(ind)"
        >Внести</button>
      </div>

      <div
        v-if="opened === ind.id"
        class="hbt-frame"
        style="margin: 12px 0; display: flex; flex-wrap: wrap; align-items: flex-end; gap: 10px;"
      >
        <div>
          <label class="hbt-ilabel">{{ isDual(ind) ? 'Систолическое' : 'Значение' }}</label>
          <input
            v-model="input1"
            type="number"
            step="0.1"
            class="hbt-input"
            style="width: 110px; font-size: 18px;"
          >
        </div>
        <div v-if="isDual(ind)">
          <label class="hbt-ilabel">Диастолическое</label>
          <input
            v-model="input2"
            type="number"
            class="hbt-input"
            style="width: 110px; font-size: 18px;"
          >
        </div>
        <button
          type="button"
          class="hbt-btn hbt-btn-acc"
          :disabled="busy"
          @click="save(ind)"
        >Сохранить</button>
      </div>
    </div>

    <div
      v-if="widget.param.showlegend !== false"
      class="d-flex flex-wrap"
      style="gap: 16px; margin-top: 14px;"
    >
      <span
        v-for="z in legend"
        :key="z.label"
        class="d-inline-flex align-items-center hbt-mut"
        style="gap: 7px; font-size: 12px;"
      >
        <span
          style="width: 12px; height: 12px; display: inline-block; border: 1px solid;"
          :style="{ background: z.bg, borderColor: z.border }"
        />{{ z.label }}
      </span>
    </div>
  </div>
</template>

<script>
import useCubus from '@/cubus/services/useCubus'
import {
  HBT_REFRESH, HBT_ZONES, hbtLoadIndicators, hbtLoadMeasures, hbtZone, hbtFmtAt, hbtAddMeasure,
} from '@/cubus/components/dashboard/habits/habitsCore'

export default {
  name: 'CbsWidgetHabitHealth',
  props: {
    widget: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      indicators: [],
      measures: [],
      opened: null,
      input1: '',
      input2: '',
      busy: false,
    }
  },
  computed: {
    legend() {
      return Object.values(HBT_ZONES)
    },
  },
  created() {
    this.load()
    this.$root.$on(HBT_REFRESH, this.reload)
  },
  beforeDestroy() {
    this.$root.$off(HBT_REFRESH, this.reload)
  },
  methods: {
    async load() {
      this.indicators = await hbtLoadIndicators(this, this.widget.param)
      await this.reload()
    },
    async reload() {
      const days = Number(this.widget.param.history) || 200
      this.measures = await hbtLoadMeasures(this, this.widget.param, days)
    },
    seriesOf(ind) {
      return this.measures.filter(m => m.indicatorId === ind.id)
    },
    lastOf(ind) {
      return this.seriesOf(ind)[0] || null
    },
    prevOf(ind) {
      return this.seriesOf(ind)[1] || null
    },
    isDual(ind) {
      return ind.sid === 'bp'
    },
    fmtValue(ind, m) {
      if (!m || m.value === null) return '—'
      if (this.isDual(ind) && m.value2 !== null) return `${m.value}/${m.value2}`
      return String(m.value)
    },
    valueText(ind) {
      return this.fmtValue(ind, this.lastOf(ind))
    },
    atOf(ind) {
      const m = this.lastOf(ind)
      return m ? hbtFmtAt(m.date) : ''
    },
    trendText(ind) {
      const prev = this.prevOf(ind)
      if (!prev) return 'первый замер'
      return `было ${this.fmtValue(ind, prev)}`
    },
    zoneOf(ind) {
      const m = this.lastOf(ind)
      return hbtZone(ind, m ? m.value : null)
    },
    zoneLabel(ind) {
      return HBT_ZONES[this.zoneOf(ind)].label
    },
    zoneStyle(ind) {
      const z = HBT_ZONES[this.zoneOf(ind)]
      return { background: z.bg, color: z.color, borderColor: z.border }
    },
    toggleInput(ind) {
      this.opened = this.opened === ind.id ? null : ind.id
      this.input1 = ''
      this.input2 = ''
    },
    async save(ind) {
      const v1 = parseFloat(this.input1)
      if (!v1) return
      const v2 = this.isDual(ind) ? parseInt(this.input2, 10) : null
      if (this.isDual(ind) && !v2) return
      this.busy = true
      try {
        const saved = await hbtAddMeasure(this, this.widget.param, ind, v1, v2)
        if (saved) {
          useCubus.toastMessage(this, `${ind.name}: ${this.isDual(ind) ? `${v1}/${v2}` : v1} — сохранено`)
          this.opened = null
          await this.reload()
          this.$root.$emit(HBT_REFRESH)
        }
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style src="./habits/habits.css"></style>
<style scoped>
.hbt-ilabel {
  display: block;
  font-size: 12px;
  color: rgba(32, 30, 29, 0.7);
  margin-bottom: 5px;
}
</style>
